import {
  addToDate,
  getEndOfDay,
  Organization,
  useNetworkStatus,
  useOrganization,
} from '@airship-platform/airship-patterns';
import { PropsWithChildren, useCallback, useRef } from 'react';
import {
  AppointmentProposal,
  AppointmentProposalCreate,
  AppointmentProposalsContext,
  AppointmentProposalsProvider,
  useAppointmentProposals,
} from '../../../data-access/appointmentProposals';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import { createOfflineUUID } from '../../rxdb/utils/rxDbOperations';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { useEntityReplication } from '../../rxdb/utils/useEntityReplication';
import { useAppointmentProposalSync } from '../appointmentProposalSync';

const DEFAULT_PRESENTATION_CONFIG: AppointmentProposal['presentationConfig'] = {
  headline: '',
  subheadline: '',
  theme: 'AIRSHIP_BLUE',
  showAIOptionsSummary: true,
  selectedAppointmentProposalOptionIds: [],
};

type ProposalConfiguration = Partial<
  Pick<Organization['proposalConfiguration'], 'expirationDays' | 'defaultTheme'>
>;

const buildOfflineProposal = (
  appointmentId: string,
  create: AppointmentProposalCreate,
  proposalConfiguration?: ProposalConfiguration
): AppointmentProposal => ({
  ...create,
  appointmentProposalId: createOfflineUUID(),
  appointmentId,
  entityType: 'APPOINTMENT_PROPOSAL',
  status: 'IN-PROGRESS',
  surveyType: 'DEFAULT',
  expiryDate: getEndOfDay(
    addToDate(proposalConfiguration?.expirationDays || 365, 'd')
  ).toISOString(),
  presentationConfig: {
    ...DEFAULT_PRESENTATION_CONFIG,
    theme:
      proposalConfiguration?.defaultTheme || DEFAULT_PRESENTATION_CONFIG.theme,
  },
});

const OfflineAppointmentProposalsOverride: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const context = useAppointmentProposals();
  const data = useOfflineDbData();
  const {
    currentProposal,
    upsertProposals,
    settleProposals,
    selectAppointmentProposals,
    selectProposalId,
    setProposalMutating,
  } = context;
  const { organization } = useOrganization();
  const { syncAppointment } = useAppointmentProposalSync();
  const isOnline = useNetworkStatus();

  const getProposalDocs = useCallback(
    (appointmentId: string, includeDeleted = false) =>
      data
        .find('appointment_proposals', { selector: { appointmentId } })
        .then((docs) =>
          includeDeleted ? docs : docs.filter((d) => d.status !== 'DELETED')
        ),
    [data]
  );

  // Ref keeps onSync's identity stable across currentProposal changes.
  const currentAppointmentIdRef = useRef(currentProposal?.appointmentId);
  currentAppointmentIdRef.current = currentProposal?.appointmentId;

  const onSync = useCallback(async () => {
    if (currentAppointmentIdRef.current) {
      const docs = await getProposalDocs(currentAppointmentIdRef.current, true);
      upsertProposals(docs);
    }
  }, [getProposalDocs, upsertProposals]);

  const { initError } = useEntityReplication(
    'appointment_proposals',
    'airship-appointment-proposals',
    { onSync, upsert: upsertProposals }
  );

  const fetchAppointmentProposals: typeof context.fetchAppointmentProposals =
    useCallback(
      async (appointmentId) => {
        selectAppointmentProposals(appointmentId);

        const docs = await getProposalDocs(appointmentId);
        if (docs.length) {
          settleProposals(docs);
        }

        await syncAppointment(appointmentId);
        const synced = await getProposalDocs(appointmentId);
        settleProposals(synced);

        return synced;
      },
      [
        getProposalDocs,
        settleProposals,
        selectAppointmentProposals,
        syncAppointment,
      ]
    );

  const fetchAppointmentProposal: typeof context.fetchAppointmentProposal =
    useCallback(
      async (appointmentId, appointmentProposalId, options) => {
        selectProposalId(appointmentId, appointmentProposalId);

        const getDoc = () =>
          data
            .findOne('appointment_proposals', appointmentProposalId)
            .then((doc) => (doc && doc.status !== 'DELETED' ? doc : undefined));

        const item = await getDoc();
        if (!item && !isOnline()) {
          settleProposals([], {
            error: 'Proposal not found offline',
            errorKind: 'NOT_FOUND',
          });
          return undefined;
        }

        if (item) {
          settleProposals([item]);
        }

        await syncAppointment(appointmentId);
        if (options?.signal?.aborted) {
          return item;
        }

        const synced = await getDoc();
        settleProposals(
          synced ? [synced] : [],
          synced ? {} : { error: 'Proposal not found', errorKind: 'NOT_FOUND' }
        );

        return synced;
      },
      [data, selectProposalId, syncAppointment, settleProposals, isOnline]
    );

  const createAppointmentProposal: typeof context.createAppointmentProposal =
    useCallback(
      (appointmentId, create) => {
        setProposalMutating();
        return data
          .insertSynced(
            'appointment_proposals',
            buildOfflineProposal(appointmentId, create, {
              expirationDays:
                organization?.proposalConfiguration.expirationDays,
              defaultTheme: organization?.proposalConfiguration.defaultTheme,
            }),
            0
          )
          .then((res) => {
            settleProposals([res]);
            return res;
          })
          .catch((error) => {
            settleProposals([], {
              error: 'We were unable to create the proposal. Please try again.',
              errorKind: 'CREATE',
            });
            throw error;
          });
      },
      [
        data,
        organization?.proposalConfiguration.expirationDays,
        organization?.proposalConfiguration.defaultTheme,
        setProposalMutating,
        settleProposals,
      ]
    );

  const updateAppointmentProposal: typeof context.updateAppointmentProposal =
    useCallback(
      (_appointmentId, appointmentProposalId, update) => {
        setProposalMutating();
        return data
          .patchSynced(
            'appointment_proposals',
            appointmentProposalId,
            { ...update, offlineSyncErrors: null },
            0
          )
          .then((res) => {
            settleProposals([res]);
            return res;
          })
          .catch((error) => {
            settleProposals([], {
              error:
                'We were unable to save your proposal changes. Please try again.',
              errorKind: 'UPDATE',
            });
            throw error;
          });
      },
      [data, setProposalMutating, settleProposals]
    );

  const appendSelectedAppointmentProposalOptionsIds: typeof context.appendSelectedAppointmentProposalOptionsIds =
    useCallback(
      (newOptionIds) =>
        currentProposal?.presentationConfig.selectedAppointmentProposalOptionIds
          ?.length
          ? updateAppointmentProposal(
              currentProposal.appointmentId,
              currentProposal.appointmentProposalId,
              {
                presentationConfig: {
                  ...currentProposal.presentationConfig,
                  selectedAppointmentProposalOptionIds: Array.from(
                    new Set([
                      ...currentProposal.presentationConfig
                        .selectedAppointmentProposalOptionIds,
                      ...newOptionIds,
                    ])
                  ),
                },
              }
            )
          : Promise.resolve(),
      [
        currentProposal?.presentationConfig,
        currentProposal?.appointmentId,
        currentProposal?.appointmentProposalId,
        updateAppointmentProposal,
      ]
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="appointment proposals"
      />
    );
  }

  return (
    <AppointmentProposalsContext.Provider
      value={{
        ...context,
        fetchAppointmentProposal,
        fetchAppointmentProposals,
        createAppointmentProposal,
        updateAppointmentProposal,
        appendSelectedAppointmentProposalOptionsIds,
      }}
    >
      {children}
    </AppointmentProposalsContext.Provider>
  );
};

export const OfflineAppointmentProposalsProvider: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <AppointmentProposalsProvider>
    <OfflineAppointmentProposalsOverride>
      {children}
    </OfflineAppointmentProposalsOverride>
  </AppointmentProposalsProvider>
);
